import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridActionsCellItem } from '@mui/x-data-grid';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { buildPath, nameof } from '@e-socna/utils';

import { PATH_MATERIAL_DEFAULT } from '../../../../app/constants/paths.constant';
import type { ReferencesContext_GenericStatus } from '../../../../contexts/ReferencesContext';

import type { ResolvableServicePage_MaterialUsagesList_MaterialUsage } from '.';

export const MATERIAL_STATUS_ID = 'materialStatus';

interface Props {
  readonly materialUsage: Readonly<ResolvableServicePage_MaterialUsagesList_MaterialUsage>;
}

const Action = ({ materialUsage }: Readonly<Props>) => {
  const navigate = useNavigate();

  const consult = useCallback(() => {
    const path = buildPath(PATH_MATERIAL_DEFAULT, {
      id: materialUsage.material._id,
    });
    void navigate(path);
  }, [navigate, materialUsage.material._id]);

  const actionLabel = 'Consulter le Matériel';

  return (
    <GridActionsCellItem
      icon={
        <Tooltip title={actionLabel}>
          <VisibilityIcon />
        </Tooltip>
      }
      label={actionLabel}
      onClick={consult}
    />
  );
};

export const getColumns = (
  genericStatuses: readonly ReferencesContext_GenericStatus[],
): readonly GridColDef<ResolvableServicePage_MaterialUsagesList_MaterialUsage>[] => [
  {
    field: 'actions',
    headerName: 'Actions',
    description: 'Actions',
    editable: false,
    sortable: false,
    type: 'actions',
    getActions: (
      params: Readonly<
        GridRowParams<ResolvableServicePage_MaterialUsagesList_MaterialUsage>
      >,
    ) => [<Action key="consult" materialUsage={params.row} />],
  },
  {
    field: 'materialCode',
    headerName: 'Code',
    width: 200,
    description: 'Code',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<ResolvableServicePage_MaterialUsagesList_MaterialUsage>,
    ) => row.material.code,
  },
  {
    field: 'materialLabel',
    headerName: 'Libellé',
    width: 400,
    description: 'Libellé',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<ResolvableServicePage_MaterialUsagesList_MaterialUsage>,
    ) => row.material.label,
  },
  {
    field:
      nameof<ResolvableServicePage_MaterialUsagesList_MaterialUsage>(
        'quantity',
      ),
    headerName: 'Quantité',
    width: 200,
    description: 'Quantité',
    editable: false,
    sortable: true,
    type: 'number',
    valueGetter: (
      _: unknown,
      row: Readonly<ResolvableServicePage_MaterialUsagesList_MaterialUsage>,
    ) => row.quantity.value,
  },
  {
    field: MATERIAL_STATUS_ID,
    headerName: 'Statut',
    width: 200,
    description: 'Statut',
    editable: false,
    sortable: true,
    hideable: true,
    type: 'singleSelect',
    valueGetter: (
      _: unknown,
      row: Readonly<ResolvableServicePage_MaterialUsagesList_MaterialUsage>,
    ) =>
      genericStatuses.find(
        (genericStatus) => genericStatus._id === row.material.statusId,
      )?.code ?? 'Inconnu',
    valueOptions: genericStatuses.map((genericStatus) => ({
      value: genericStatus.code,
      label: genericStatus.label,
    })),
  },
];
